import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { Link, useLocation } from "wouter";

type NavLink = {
  name: string;
  href: string;
  isHash: boolean;
  badge?: string;
  children?: NavLink[];
};

const navLinks: NavLink[] = [
  { name: "Home", href: "/", isHash: false },
  {
    name: "What We Do",
    href: "",
    isHash: false,
    children: [
      { name: "Corporate Videos", href: "/work", isHash: false },
      { name: "Brand Stand", href: "/brand-stand", isHash: false },
      { name: "Legacy Forge", href: "/legacy-forge", isHash: false },
      { name: "Indie Films", href: "/indie-film", isHash: false },
      { name: "Vertical Dramas", href: "/vertical-micro-dramas", isHash: false },
      { name: "Creative & Marketing", href: "/creative-marketing", isHash: false },
    ],
  },
  {
    name: "About Us",
    href: "",
    isHash: false,
    children: [
      { name: "About Black Swan Productions", href: "/about", isHash: false },
      { name: "Memory Wall", href: "/memory-wall", isHash: false },
    ],
  },
  { name: "News & Blogs", href: "/news", isHash: false },
  { name: "Success Stories", href: "/success-stories", isHash: false },
  { name: "Contact Us", href: "/contact", isHash: false },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [location, setLocation] = useLocation();
  const baseUrl = import.meta.env.BASE_URL;

  // Fill header background once the page is scrolled
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, link: typeof navLinks[0]) => {
    if (link.isHash) {
      e.preventDefault();
      setIsOpen(false);
      
      if (location !== "/") {
        // Defer scrolling until the home route mounts (handled in App's ScrollToTop)
        sessionStorage.setItem("pendingScrollTarget", link.href);
        setLocation("/");
      } else {
        const element = document.querySelector(link.href);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: "smooth" });
          }, 500); // Wait for menu close animation
        }
      }
    } else {
      // For non-hash links, let wouter's Link handle the routing
      setIsOpen(false);
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location !== "/") {
      setLocation("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const menuVariants = {
    closed: {
      opacity: 0,
      y: "-100%",
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1] as const,
      }
    },
    open: {
      opacity: 1,
      y: "0%",
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1] as const,
      }
    }
  };

  const linkVariants = {
    closed: { y: 50, opacity: 0 },
    open: (i: number) => ({
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1] as const,
        delay: 0.3 + i * 0.1,
      }
    })
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-[60] px-6 transition-[background-color,backdrop-filter,padding] duration-300 ${scrolled ? 'py-3' : 'py-6'} ${
          isOpen
            ? ''
            : scrolled
              ? 'bg-black/80 backdrop-blur-md pointer-events-none'
              : 'pointer-events-none'
        }`}
      >
        <div className="container mx-auto flex items-center justify-between">
          <a href="/" onClick={handleLogoClick} className={`pointer-events-auto ${isOpen ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}>
            <img 
              src={`${baseUrl}brand/logo-secondary.png`} 
              alt="Black Swan" 
              className="h-10 w-auto object-contain"
            />
          </a>
          <div className="flex items-center gap-6">
            <Link 
              href="/contact"
              onClick={(e) => {
                if (isOpen) {
                  setIsOpen(false);
                }
              }}
              className={`hidden sm:inline-flex pointer-events-auto ${isOpen ? 'opacity-0' : 'opacity-100'} transition-all duration-300 rounded-full border border-white/30 px-6 py-2.5 text-xs font-display uppercase tracking-widest font-semibold text-white hover:bg-primary hover:border-primary items-center justify-center`}
            >
              Get In Touch
            </Link>
            <button 
              onClick={() => setIsOpen(!isOpen)}
              className="pointer-events-auto relative w-10 h-10 flex items-center justify-center group focus:outline-none"
              aria-label="Toggle menu"
            >
            <div className="flex flex-col items-end gap-2 w-8">
              <motion.div 
                initial={false}
                animate={isOpen ? { rotate: 45, y: 10 } : { rotate: 0, y: 0 }}
                className={`h-[2px] w-full bg-white transition-colors group-hover:bg-primary`}
              />
              <motion.div 
                initial={false}
                animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
                className={`h-[2px] w-3/4 bg-white transition-colors group-hover:bg-primary`}
              />
              <motion.div 
                initial={false}
                animate={isOpen ? { rotate: -45, y: -10, width: "100%" } : { rotate: 0, y: 0, width: "50%" }}
                className={`h-[2px] w-1/2 bg-white transition-colors group-hover:bg-primary`}
              />
            </div>
          </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="fixed inset-0 z-50 bg-black overflow-y-auto overflow-x-hidden"
          >
            {/* Background branding subtle */}
            <div className="fixed inset-0 z-0 opacity-10 pointer-events-none flex items-center justify-center">
               <img src={`${baseUrl}brand/mark.png`} alt="" className="w-1/2 object-contain" />
            </div>

            <div className="relative min-h-full flex flex-col justify-center px-6 md:px-16 lg:px-24 py-24">
            <nav className="relative z-10 flex flex-col gap-3 md:gap-4">
              {navLinks.map((item, i) => (
                <div key={item.name} className="overflow-hidden">
                  <motion.div
                    custom={i}
                    variants={linkVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                  >
                    {item.children && !item.href ? (
                      <>
                        <div className="font-display text-2xl md:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white">
                          {item.name}
                        </div>
                        <div className="ml-5 mt-2 flex flex-col gap-1 border-l border-white/15 pl-5 md:ml-7 md:pl-7">
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              href={child.href}
                              onClick={(e) => handleLinkClick(e as any, child)}
                              className="inline-flex w-fit items-center font-display text-lg md:text-xl lg:text-[22px] font-medium text-gray-300 hover:text-primary transition-colors duration-300"
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </>
                    ) : item.isHash ? (
                      <a
                        href={item.href}
                        onClick={(e) => handleLinkClick(e, item)}
                        className="font-display text-2xl md:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white hover:text-primary transition-colors duration-500 inline-block"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <div className="flex flex-col items-start">
                        <Link
                          href={item.href}
                          onClick={(e) => handleLinkClick(e as any, item)}
                          className="font-display text-2xl md:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white hover:text-primary transition-colors duration-500 inline-flex items-center"
                        >
                          {item.name}
                          {item.badge && (
                            <span className="ml-3 align-middle inline-flex items-center rounded-md bg-primary-strong px-2.5 py-1 text-[0.6rem] font-display font-semibold uppercase tracking-[0.2em] text-white">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                        {item.children?.length ? (
                          <div className="ml-5 mt-2 flex flex-col gap-1 border-l border-white/15 pl-5 md:ml-7 md:pl-7">
                            {item.children.map((child) => (
                              <Link
                                key={child.name}
                                href={child.href}
                                onClick={(e) => handleLinkClick(e as any, child)}
                                className="inline-flex w-fit font-display text-lg md:text-xl lg:text-[22px] font-medium text-gray-300 hover:text-primary transition-colors duration-300"
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </motion.div>
                </div>
              ))}
            </nav>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="relative z-10 mt-40 pb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t border-white/10 pt-8"
            >
              <div className="flex flex-col gap-2 min-w-0">
                <a 
                  href="mailto:Contact@blackswanproductions.co.uk"
                  className="text-base sm:text-lg md:text-xl text-gray-400 hover:text-white transition-colors duration-300 break-words"
                >
                  Contact@blackswanproductions.co.uk
                </a>
                <p className="text-sm md:text-base text-gray-400 not-italic">
                  Great Point Studios, Wentloog Ave, Cardiff, CF3 2GH
                </p>
              </div>
              
              <div className="flex gap-6">
                <a href="https://www.instagram.com/blackswan_productions/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-gray-400 hover:text-primary transition-colors duration-300">
                  <Instagram className="w-6 h-6" />
                </a>
                <a href="https://www.facebook.com/blackswanproductionsltd" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-gray-400 hover:text-primary transition-colors duration-300">
                  <Facebook className="w-6 h-6" />
                </a>
                <a href="https://www.linkedin.com/company/blackswanproductions/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-gray-400 hover:text-primary transition-colors duration-300">
                  <Linkedin className="w-6 h-6" />
                </a>
                <a href="https://www.youtube.com/@BlackSwanProductions/videos" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-gray-400 hover:text-primary transition-colors duration-300">
                  <Youtube className="w-6 h-6" />
                </a>
              </div>
            </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
